import React from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { Box, Button, Container, Typography, Paper } from '@mui/material';
import { Google as GoogleIcon } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { useNavigate, useLocation } from 'react-router-dom';

const Login: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        // With implicit flow, we get the access token directly
        // Note: implicit flow doesn't provide refresh tokens
        // For refresh tokens, we'd need to implement a backend OAuth flow
        
        // Get user info from Google
        const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: {
            Authorization: `Bearer ${tokenResponse.access_token}`,
          },
        });

        if (!userInfoResponse.ok) {
          throw new Error('Failed to fetch user info');
        }

        const userInfo = await userInfoResponse.json();

        // Store the access token and user email
        // Note: refresh_token is not available with implicit flow
        login(tokenResponse.access_token, userInfo.email);

        // Redirect to the page they were trying to access, or home page
        const from = (location.state as any)?.from?.pathname || '/';
        navigate(from, { replace: true });
      } catch (error) {
        console.error('Login failed:', error);
        alert('Login failed. Please try again.');
      }
    },
    onError: () => {
      console.error('Google login failed');
      alert('Google login failed. Please try again.');
    },
    // Use implicit flow for direct token access
    // Note: This doesn't provide refresh tokens, but works in browser-only apps
    scope: 'openid email profile',
  });

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography component="h1" variant="h4" gutterBottom>
            DocuSign Template Editor
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
            Sign in with your Google account to access the template editor
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<GoogleIcon />}
            onClick={() => handleGoogleLogin()}
            sx={{
              textTransform: 'none',
              fontSize: '1rem',
              padding: '12px 24px',
            }}
          >
            Sign in with Google
          </Button>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
